import { useCallback, useEffect, useRef } from "react";
import { DisconnectReason } from "livekit-client";
import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import { videoService } from "../api/video.api";
import { useCallSessionStore } from "../store/callSessionStore";
import { endReasonFor, type EvictionSignals } from "../lib/evictionTag";
import { rejoinDelayMs } from "../lib/rejoinBackoff";

/**
 * Auto-rejoin controller for the shell-hoisted <LiveKitRoom> (#1178, #2223).
 *
 * LiveKit's built-in reconnection (resume, then a full restart, ~45 s of
 * attempts) covers transient blips; once it gives up it fires
 * `onDisconnected`. This controller then treats a *terminal, involuntary*
 * disconnect as the start of an auto-rejoin: it re-mints a fresh token
 * (name- and grant-preserving, guest via skipAuth) and swaps it onto
 * <LiveKitRoom> to open a new connection.
 *
 * It never gives up while the session is active (#2223 AC13: "always brought
 * back, never left out" -- a bounded budget left 9 of 25 dropped participants
 * outside the call for good). Retries back off 2 s → 4 s → 8 s, then every
 * 10 s, and the browser coming back online or the page becoming visible
 * again skips the wait. Leaving stays possible at any time from the
 * reconnecting indicator. A deliberate leave (leaveIntent flag) or a
 * server-terminal reason (kick / room deleted / duplicate identity / server
 * shutdown) is NOT retried — those exit cleanly.
 */

// Belt-and-suspenders: if a token was minted and swapped in but LiveKitRoom
// emits neither onConnected nor onDisconnected, fail the attempt after this.
const WATCHDOG_MS = 15000;

// Server-terminal reasons — an auto-rejoin cannot help, so exit cleanly to the
// post-call screen rather than looping. DUPLICATE_IDENTITY and the tagged
// cross-room eviction get their own post-call copy (#1918).
const SUPPRESS_REASONS = new Set<DisconnectReason>([
  DisconnectReason.PARTICIPANT_REMOVED,
  DisconnectReason.ROOM_DELETED,
  DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.SERVER_SHUTDOWN,
]);

export interface CallReconnectController {
  /** Wire to <LiveKitRoom onDisconnected>. `signals` is what the session
   *  learned before the drop (the #1918 eviction marker + other call's name). */
  handleDisconnected: (reason?: DisconnectReason, signals?: EvictionSignals) => void;
  /** Wire to <LiveKitRoom onConnected>. */
  handleConnected: () => void;
}

export function useCallReconnect(): CallReconnectController {
  // Attempt currently in flight; 0 = connected normally / not reconnecting.
  // A ref (not state) so onDisconnected/onConnected read it synchronously.
  const attemptRef = useRef(0);
  const backoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (backoffTimer.current) {
      clearTimeout(backoffTimer.current);
      backoffTimer.current = null;
    }
    if (watchdog.current) {
      clearTimeout(watchdog.current);
      watchdog.current = null;
    }
  }, []);

  // Forward declaration so runAttempt and fail can reference each other.
  const runAttemptRef = useRef<(n: number) => void>(() => {});

  // A single attempt failed (mint threw, no creds, watchdog, or the fresh
  // connection dropped again). Schedule the next one; there is no last one.
  const fail = useCallback(
    (n: number) => {
      // Stale signal (recovery or a later attempt already advanced us).
      if (attemptRef.current !== n) return;
      clearTimers();
      const next = n + 1;
      attemptRef.current = next;
      const delay = rejoinDelayMs(next);
      backoffTimer.current = setTimeout(() => {
        backoffTimer.current = null;
        runAttemptRef.current(next);
      }, delay);
    },
    [clearTimers],
  );

  const runAttempt = useCallback(
    async (n: number) => {
      const { active } = useCallSessionStore.getState();
      if (!active) return; // session cleared (deliberate leave) — abandon.
      useCallSessionStore.getState().setReconnect("reconnecting", n);
      try {
        const authToken = await getValidAccessTokenFromStorage();
        const fresh = await videoService.getRoom(active.slug, {
          username: active.choices.username,
          skipAuth: !authToken,
        });
        // Bail if a deliberate leave / new join landed while we awaited.
        if (attemptRef.current !== n) return;
        if (!useCallSessionStore.getState().active) return;
        if (fresh.livekit?.url && fresh.livekit?.token) {
          // Swapping the token prop is what drives <LiveKitRoom> to open a
          // new connection; success → onConnected, failure → onDisconnected.
          useCallSessionStore
            .getState()
            .updateCreds(fresh.livekit.url, fresh.livekit.token);
          watchdog.current = setTimeout(() => fail(n), WATCHDOG_MS);
        } else {
          fail(n);
        }
      } catch {
        // Still offline / mint failed — count it as a failed attempt.
        fail(n);
      }
    },
    [fail],
  );
  runAttemptRef.current = runAttempt;

  // A fresh connection succeeded — cancel everything and return to idle.
  const recover = useCallback(() => {
    attemptRef.current = 0;
    clearTimers();
    useCallSessionStore.getState().setReconnect("idle", 0);
  }, [clearTimers]);

  const handleDisconnected = useCallback(
    (reason?: DisconnectReason, signals?: EvictionSignals) => {
      const store = useCallSessionStore.getState();
      // Deliberate leave (Leave/End/nav-away set the flag first) → clean exit.
      if (store.leaveIntent) {
        attemptRef.current = 0;
        clearTimers();
        // Read the room before clear() drops it. If this leave was the caller
        // abandoning a group ring nobody answered, everyone still ringing has
        // to stop (#1347 AC11); the request is a server-side no-op in every
        // other case, so it needs no group-call state here. A leave that takes
        // the page with it can lose this request — the participant_left
        // webhook is the backstop for exactly that.
        const roomId = store.active?.roomId;
        store.clear();
        if (roomId) {
          videoService.cancelGroupRing(roomId).catch(() => {});
        }
        return;
      }
      // Server-terminal reason → clean exit to post-call (no futile retry).
      // Same account took the call elsewhere → post-call says so (#1918).
      if (reason != null && SUPPRESS_REASONS.has(reason)) {
        attemptRef.current = 0;
        clearTimers();
        store.clear(endReasonFor(reason, signals));
        return;
      }
      // Involuntary network/signal drop.
      if (attemptRef.current === 0) {
        // First drop → start rejoining.
        attemptRef.current = 1;
        runAttempt(1);
      } else {
        // A reconnect attempt's connection dropped again → fail this attempt.
        fail(attemptRef.current);
      }
    },
    [clearTimers, fail, runAttempt],
  );

  const handleConnected = useCallback(() => {
    // Only meaningful mid-reconnect; the initial connect leaves attemptRef 0.
    if (attemptRef.current > 0) recover();
  }, [recover]);

  // Network back or page visible again while waiting out a backoff: try now
  // rather than sit out up to 10 s of an already-working connection.
  useEffect(() => {
    const tryNow = () => {
      if (document.visibilityState !== "visible") return;
      if (attemptRef.current === 0 || !backoffTimer.current) return;
      clearTimeout(backoffTimer.current);
      backoffTimer.current = null;
      runAttemptRef.current(attemptRef.current);
    };
    window.addEventListener("online", tryNow);
    document.addEventListener("visibilitychange", tryNow);
    return () => {
      window.removeEventListener("online", tryNow);
      document.removeEventListener("visibilitychange", tryNow);
    };
  }, []);

  // Cancel any in-flight timers if the wrapper unmounts (session ended).
  useEffect(() => () => clearTimers(), [clearTimers]);

  return { handleDisconnected, handleConnected };
}
